import { Component, Input, OnInit, input, model } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { faEdit, faFileLines, faFileArrowDown, faTrash, faMessage, faArrowRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { EditFireComponent } from 'src/app/routes/fire-tracker/edit-fire/edit-fire.component';
import { PrintReport25008Component } from 'src/app/routes/report-2500-8/view-sections/print-report/print-report-2500-8.component';
import { DeleteFireComponent } from "../../../fire-tracker/delete-fire/delete-fire.component";
import { ConfirmationToastComponent } from "../../../../components/confirmation-toast/confirmation-toast.component";
import { SendBackReportComponent } from 'src/app/components/send-back-report/send-back-report.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-view-reports',
  templateUrl: './view-reports.component.html',
  styleUrl: './view-reports.component.scss',
  standalone: false
})
export class ViewReportsComponent implements OnInit {

  faEdit = faEdit;
  faFileLines = faFileLines;
  faFileArrowDown = faFileArrowDown;
  faTrash = faTrash;
  faMessage = faMessage;
  faArrowRotateLeft = faArrowRotateLeft;

  reportsTable = new MatTableDataSource<any>();
  displayedColumns: string[] = ['name', 'reportId', 'action_type', 'report_type', 'funding_requested', 'authorized_funding', 'sumbission_date', 'reportStatus', 'authorization_status', 'comments', 'action'];

  edit_fire = model<boolean>();
  @Input() fire?: any;
  @Input() reports!: any;
  @Input() updateFireSubject?: Subject<any>;
  @Input() isTeamLead!: boolean;
  @Input() isROCO: any = false;
  @Input() isWOCO: any = false;
  @Input() user: any;
  editable = input.required<boolean>();

  constructor(
    public dialog: MatDialog,
    public readonly toast: ConfirmationToastComponent,
    public readonly router: Router,
  ) { }

  ngOnInit() {
    this.setReportActions(this.reports)
    this.reports.forEach(this.calcRequestedFunding)
    this.reports.forEach(this.calcAuthorizedFunding)
    this.reportsTable.data = this.reports;
    this.updateFireSubject?.subscribe((res: any) => {
      if (res?.reports) {
        this.setReportActions(res.reports)
        this.reportsTable.data = res.reports;
      }
    })
  }

  isDeletable(report: any) {
    return this.user.isWO && (report.report_status_id === 0 || !this.fire.tentative_start_week || new Date() < new Date(this.fire.tentative_start_week));
  }
  isAbleToSendBack(report: any) {
    return (this.user.isWO && (report.report_status_id === 2) && (report.authorization_status_id <= 5));
  }

  onDelete(report: any) {
    this.dialog.open(DeleteFireComponent, {
      maxWidth: "900px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        fireRecord: this.fire,
        report,
      },
    }).afterClosed().subscribe((reload) => {
      if (reload) {
        this.toast.openSuccessSnackBar('', '', `<strong>${this.fire.baer_name} (${report.report_num}) report</strong> was successfully deleted.`)
        this.reports.splice(this.reports.findIndex((r: any) => r.report_id === report.report_id), 1);
        this.reportsTable.data = this.reports;
      }
    });
  }

  stopPropagation(event: any) {
    event.stopPropagation();
  }

  edit() {
    this.dialog.open(EditFireComponent, {
      maxWidth: "900px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        fireRecord: this.fire,
      },
    }).afterClosed().subscribe((reload: boolean) => {
      if (reload) {
        this.updateFireSubject?.next(null)
      }
    })
  }

  /**
   * Set list of actions for each report based on user permissions
   * @param reports
   */
  setReportActions(reports: any) {
    reports.forEach((report: any) => {
      let edit_ids = report.users.filter((user: any) => user.editor)?.map((x: any) => x.user_id) ?? [];
      edit_ids.push(report.assessment.team_lead_id);
      let actions = [];
      if (report.report_status_id != 2) { // SUBMITTED
        if (((this.isROCO?.length ?? 0) > 0) || (edit_ids.includes(this.user.user_id) || this.editable())) {
          actions.push("edit");
        }
      }
      else if (((this.isWOCO?.length >= 1) || ((this.isROCO?.length ?? 0) > 0)) && [0, 1, 2, 3].includes(report.authorization_status_id)) {
        actions.push("edit");
      }
      actions.push("view", "download");
      if (this.isDeletable(report)) {
        actions.push("delete");
      }
      if (this.isAbleToSendBack(report)) {
        actions.push("revert");
      }
      report.actions = actions;
    });
  }

  calcRequestedFunding(report: any) {
    let cost = 0;
    for (let forest of report.forests) {
      for (let treatment of forest.treatments) {
        for (let spec of treatment.treatment_specifications) {
          cost += spec.unit_cost * (spec.custom_unit_qty ?? spec.gis_unit_qty);
        }
      }
    }
    report.funding_requested = cost;
  }

  calcAuthorizedFunding(report: any) {
    let cost = 0;
    for (let forest of report.forests) {
      for (let treatment of forest.treatments) {
        for (let spec of treatment.treatment_specifications) {
          cost += ((spec.approved_unit_qty ?? 0) * (spec.approved_unit_cost ?? 0)) + ((spec.wo_approved_unit_qty ?? 0) * (spec.wo_approved_unit_cost ?? 0));
        }
      }
    }

    report.authorized_funding = cost;
  }

  download(report_id: any) {
    this.dialog.open(PrintReport25008Component, {
      width: "1700px",
      height: "0px",
      data: {
        report_id: report_id
      }
    });
  }

  sendBack(report: any): void {
    this.dialog.open(SendBackReportComponent, {
      width: "440px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        report: report
      }
    }).afterClosed().subscribe((reload) => {
      if (reload) {
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate(['fire-overview'], { queryParams: { assessment_id: this.fire.assessment_id } });
        });
      }
    })
  }
}
